import { apiClient } from './client';
import {
  ChartBucket,
  ChartPeriod,
  ConversionMetrics,
  CounselorDashboard,
  CounselorRankingEntry,
  DashboardStats,
  FollowUpDashboard,
  PendingLeadsByCounselor,
} from '../types/dashboard';

/** Phase 76 branch selector: every function below (except the Counselor's
 * own self-view, which is always scoped to the caller alone) accepts an
 * optional `branchId` — passed through untouched from `useBranchFilter()`'s
 * `selectedBranchId`. The backend only ever honors it for a global-scope
 * caller (Super Admin/Lead Manager); for anyone else it's silently ignored
 * in favor of their own branch, so sending it is always safe. */
export const getDashboardStats = async (branchId?: number): Promise<DashboardStats> => {
  const { data } = await apiClient.get('/dashboard/stats', { params: { branchId } });
  return data.data;
};

/** `activeOnly` excludes inactive employees in the backend query itself —
 * used by the Counselor Leaderboard. Omitted for callers that still need
 * inactive Counselors too (Leads of Inactive Counselors, Transfer Leads). */
export const getCounselorRanking = async (activeOnly?: boolean, branchId?: number): Promise<CounselorRankingEntry[]> => {
  const { data } = await apiClient.get('/dashboard/counselor-ranking', { params: { activeOnly, branchId } });
  return data.data;
};

export const getConversionMetrics = async (branchId?: number): Promise<ConversionMetrics> => {
  const { data } = await apiClient.get('/dashboard/conversion', { params: { branchId } });
  return data.data;
};

export const getLeadChart = async (period: ChartPeriod, branchId?: number): Promise<ChartBucket[]> => {
  const { data } = await apiClient.get('/dashboard/chart', { params: { period, branchId } });
  return data.data;
};

export const getFollowUpDashboard = async (branchId?: number): Promise<FollowUpDashboard> => {
  const { data } = await apiClient.get('/dashboard/follow-ups', { params: { branchId } });
  return data.data;
};

export const getPendingDocumentsCount = async (branchId?: number): Promise<number> => {
  const { data } = await apiClient.get('/dashboard/pending-documents', { params: { branchId } });
  return data.data.count;
};

export interface DocumentReviewCounts {
  approved: number;
  rejected: number;
}

export const getDocumentReviewCounts = async (branchId?: number): Promise<DocumentReviewCounts> => {
  const { data } = await apiClient.get('/dashboard/document-review-counts', { params: { branchId } });
  return data.data;
};

export const getRegisteredStudentsCount = async (branchId?: number): Promise<number> => {
  const { data } = await apiClient.get('/dashboard/registered-students-count', { params: { branchId } });
  return data.data.count;
};

export const getCounselorDashboard = async (): Promise<CounselorDashboard> => {
  const { data } = await apiClient.get('/dashboard/counselor');
  return data.data;
};

export const getCounselorLeadChart = async (period: ChartPeriod): Promise<ChartBucket[]> => {
  const { data } = await apiClient.get('/dashboard/counselor-chart', { params: { period } });
  return data.data;
};

export const getPendingLeadsByCounselor = async (branchId?: number): Promise<PendingLeadsByCounselor> => {
  const { data } = await apiClient.get('/dashboard/pending-leads-by-counselor', { params: { branchId } });
  return data.data;
};
